import { useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NeuSelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  /** Extra search terms (space-separated) matched by the `searchable` filter in
   *  addition to the visible label. Lets a user find an option by a synonym the
   *  label doesn't contain — e.g. typing "convertidor" to reach the "VDF" pauta. */
  keywords?: string;
}

interface NeuSelectProps {
  options: NeuSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Opt-in filter input at the top of the dropdown (obs 12). Off by default so
   *  every existing NeuSelect is unchanged. */
  searchable?: boolean;
}

export function NeuSelect({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  label,
  error,
  disabled,
  className,
  'aria-label': ariaLabel,
  searchable = false,
}: NeuSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const [query, setQuery] = useState('');

  const selected = options.find((o) => o.value === value);

  // Filtered options drive both the rendered list and keyboard navigation, so
  // focusIdx always indexes into `visibleOptions` (not the full list).
  const visibleOptions = useMemo(() => {
    if (!searchable || query.trim() === '') return options;
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.keywords !== undefined && o.keywords.toLowerCase().includes(q)),
    );
  }, [options, searchable, query]);

  // Reset the query whenever the dropdown closes, and focus the input on open.
  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    if (searchable) {
      // Defer so the input is mounted before focusing.
      const t = setTimeout(() => { searchRef.current?.focus(); }, 0);
      return () => { clearTimeout(t); };
    }
  }, [open, searchable]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); };
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      if (!open) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setOpen(true);
          setFocusIdx(visibleOptions.findIndex((o) => o.value === value));
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusIdx((i) => Math.min(i + 1, visibleOptions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusIdx((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
        case ' ': {
          // Space must be typable in the search box — don't treat it as select.
          if (e.key === ' ' && searchable) break;
          e.preventDefault();
          if (focusIdx >= 0 && visibleOptions[focusIdx]) {
            onChange(visibleOptions[focusIdx].value);
            setOpen(false);
          }
          break;
        }
        case 'Escape':
          e.preventDefault();
          // First Escape clears the query; a second closes (handled by re-press
          // since query becomes empty). If no query, close immediately.
          if (searchable && query.trim() !== '') {
            setQuery('');
          } else {
            setOpen(false);
          }
          break;
      }
    },
    [open, disabled, visibleOptions, value, focusIdx, onChange, searchable, query],
  );

  // Scroll focused item into view
  useEffect(() => {
    if (!open || focusIdx < 0 || !listRef.current) return;
    const item = listRef.current.children[focusIdx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [open, focusIdx]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {label ? (
        <label className="block text-sm font-medium text-sime-text-secondary mb-1.5">
          {label}
        </label>
      ) : null}

      {/* Trigger button */}
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel ?? label}
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full flex items-center justify-between gap-2 rounded-xl px-4 py-3 min-h-[44px]',
          'bg-transparent text-sm text-left',
          // Explicit border for dark-mode AMOLED visibility.
          'border border-sime-border-subtle',
          'shadow-neu-btn-active',
          'focus:ring-2 focus:ring-sime-accent focus:outline-none',
          'transition-shadow duration-150',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          error && 'ring-2 ring-sime-danger',
        )}
      >
        <span className={cn(
          'truncate',
          selected ? 'text-sime-text-primary' : 'text-sime-text-tertiary',
        )}>
          {selected ? (
            <span className="inline-flex items-center gap-2">
              {selected.icon}
              {selected.label}
            </span>
          ) : placeholder}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-sime-text-tertiary transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>

      {/* Dropdown */}
      {open ? (
        <div
          className={cn(
            'absolute z-50 mt-2 w-full rounded-xl overflow-hidden',
            'bg-sime-surface border border-sime-border-subtle',
            'shadow-neu-raised',
            'animate-in fade-in-0 zoom-in-95 duration-100',
          )}
        >
          {searchable ? (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-sime-border-subtle">
              <Search className="h-4 w-4 shrink-0 text-sime-text-tertiary" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setFocusIdx(-1); }}
                onKeyDown={handleKeyDown}
                placeholder="Buscar..."
                aria-label="Buscar opción"
                className="flex-1 bg-transparent text-sm min-h-[36px] focus:outline-none placeholder:text-sime-text-tertiary text-sime-text-primary"
              />
            </div>
          ) : null}
          <ul
            ref={listRef}
            role="listbox"
            aria-activedescendant={focusIdx >= 0 ? `neu-select-opt-${focusIdx}` : undefined}
            className="py-1.5 overflow-auto max-h-[240px]"
          >
            {visibleOptions.length === 0 ? (
              <li className="px-4 py-2.5 text-sm text-sime-text-tertiary">Sin resultados</li>
            ) : (
              visibleOptions.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isFocused = idx === focusIdx;
                return (
                  <li
                    key={opt.value}
                    id={`neu-select-opt-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => { setFocusIdx(idx); }}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2.5 text-sm cursor-pointer transition-colors',
                      isSelected
                        ? 'text-sime-accent font-medium bg-sime-accent/5'
                        : 'text-sime-text-secondary',
                      // Use accent ring for focused state — bg-surface-raised was too close
                      // to bg-surface in dark mode for keyboard nav to be visible.
                      isFocused && !isSelected && 'ring-1 ring-sime-accent/40 bg-sime-accent/10 text-sime-text-primary',
                      !isFocused && !isSelected && 'hover:bg-sime-surface-raised hover:text-sime-text-primary',
                    )}
                  >
                    {opt.icon}
                    <span className="truncate">{opt.label}</span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-sime-danger mt-1.5" role="alert">{error}</p>
      ) : null}
    </div>
  );
}
